/**
 * Import Progress Card
 *
 * Live view of a campaign's contact imports inside the chat: one progress
 * bar per search (loaded/requested) plus AI-judging status. While an import
 * is running the card polls GET /campaigns/:id/searches every 5s so the
 * user watches the numbers move instead of asking "Wie weit sind wir?".
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography, Chip, LinearProgress } from '@mui/material';
import DownloadingIcon from '@mui/icons-material/Downloading';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { API_V1 } from '../../../API/APIs/APIv1';

const POLL_INTERVAL_MS = 5000;
const RUNNING_STATUSES = new Set(['running', 'loading', 'pending']);

interface ImportSearchProgress {
  name: string;
  status: string;
  isRunning: boolean;
  contactsLoaded: number;
  contactsRequested: number;
}

export interface ImportProgressCardData {
  campaignId: string;
  campaignName?: string;
  searches: ImportSearchProgress[];
  importRunning: boolean;
  totalContacts: number;
  judging?: {
    enabled: boolean;
    judgedContacts: number;
    judgingRunning: boolean;
    grades?: Record<string, number>;
  };
}

interface LegacySearch {
  name?: string;
  status?: string;
  numberOfContactsToLoad?: number;
  state?: { crawled?: number };
}

function normalizeLegacySearch(s: LegacySearch): ImportSearchProgress {
  const status = String(s.status || 'pending').toLowerCase();
  return {
    name: s.name || 'Search',
    status,
    isRunning: RUNNING_STATUSES.has(status),
    contactsLoaded: s.state?.crawled ?? 0,
    contactsRequested: s.numberOfContactsToLoad ?? 0,
  };
}

const ImportProgressCard: React.FC<{ data: ImportProgressCardData }> = ({ data }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [searches, setSearches] = useState<ImportSearchProgress[]>(data.searches || []);

  const anyRunning = useMemo(() => searches.some((s) => s.isRunning), [searches]);

  useEffect(() => {
    if (!anyRunning || !data.campaignId) return undefined;

    const interval = setInterval(() => {
      API_V1.get(`/campaigns/${data.campaignId}/searches`)
        .then((response) => {
          const list: LegacySearch[] = Array.isArray(response.data)
            ? response.data
            : (response.data?.searches as LegacySearch[]) || [];
          if (list.length > 0) setSearches(list.map(normalizeLegacySearch));
        })
        .catch(() => {
          /* transient poll errors are fine — next tick retries */
        });
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [anyRunning, data.campaignId]);

  const judging = data.judging;

  return (
    <Paper className={classes.card} elevation={0}>
      <Box className={classes.header}>
        {anyRunning ? (
          <DownloadingIcon className={classes.runningIcon} fontSize="small" />
        ) : (
          <CheckCircleIcon color="success" fontSize="small" />
        )}
        <Typography variant="subtitle2" className={classes.title}>
          {anyRunning
            ? t('importProgressRunning', 'Kontakt-Import läuft')
            : t('importProgressDone', 'Kontakt-Import')}
        </Typography>
        {data.campaignName && (
          <Typography variant="caption" color="textSecondary" noWrap>
            {data.campaignName}
          </Typography>
        )}
      </Box>

      {searches.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          {t('importProgressNoSearches', 'Noch keine Suchen konfiguriert.')}
        </Typography>
      )}

      {searches.map((search, i) => {
        const pct = search.contactsRequested > 0
          ? Math.min(100, Math.round((search.contactsLoaded / search.contactsRequested) * 100))
          : search.isRunning ? 0 : 100;
        const failed = !search.isRunning && ['error', 'account-not-found', 'quota-exceeded'].includes(search.status);
        return (
          <Box key={`${search.name}-${i}`} className={classes.searchRow}>
            <Box className={classes.searchHead}>
              <Typography variant="body2" noWrap className={classes.searchName}>
                {failed && <ErrorIcon color="error" className={classes.inlineIcon} />}
                {search.name}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {search.contactsLoaded}/{search.contactsRequested || '?'}
              </Typography>
            </Box>
            <LinearProgress
              variant={search.isRunning && pct === 0 ? 'indeterminate' : 'determinate'}
              value={pct}
              color={failed ? 'error' : search.isRunning ? 'primary' : 'success'}
              className={classes.progress}
            />
          </Box>
        );
      })}

      <Box className={classes.footer}>
        <Chip
          size="small"
          label={t('importProgressTotal', '{{count}} Kontakte importiert', { count: data.totalContacts })}
        />
        {judging?.enabled && (
          <Chip
            size="small"
            icon={<PsychologyIcon />}
            color={judging.judgingRunning ? 'default' : 'success'}
            label={
              judging.judgingRunning
                ? t('importProgressJudging', 'AI-Bewertung läuft ({{done}} bewertet)', { done: judging.judgedContacts })
                : t('importProgressJudged', '{{done}} bewertet', { done: judging.judgedContacts })
            }
          />
        )}
      </Box>
    </Paper>
  );
};

const useStyles = makeStyles()((theme) => ({
  card: {
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    maxWidth: 480,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  title: {
    fontWeight: 600,
    flexShrink: 0,
  },
  runningIcon: {
    color: theme.palette.primary.main,
    animation: 'pai-import-pulse 1.6s ease-in-out infinite',
    '@keyframes pai-import-pulse': {
      '0%': { opacity: 1 },
      '50%': { opacity: 0.4 },
      '100%': { opacity: 1 },
    },
  },
  searchRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  searchHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  searchName: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  inlineIcon: {
    fontSize: '1rem',
  },
  progress: {
    borderRadius: 3,
    height: 6,
  },
  footer: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
}));

export default ImportProgressCard;
